import { lazy, Suspense, useEffect, useRef, useState, type CSSProperties, type PointerEvent } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { AnimatePresence, motion } from 'motion/react';
import {
  ArrowRight,
  Box,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleDot,
  Layers3,
  MousePointer2,
  Orbit,
  Pause,
  Play,
  ScanLine,
  ShoppingBag,
  Sparkles,
} from 'lucide-react';
import { digitalArtifacts, experienceNodes, livingInterfaces } from './showcaseData';
import { useExperienceStore } from './store';

gsap.registerPlugin(ScrollTrigger);

const ArtifactScene = lazy(() => import('./ArtifactScene'));

function useSectionVisibility<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), {
      rootMargin: '150px 0px',
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, visible };
}

export function DigitalArtifacts() {
  const [active, setActive] = useState(0);
  const [sceneReady, setSceneReady] = useState(false);
  const [sceneActive, setSceneActive] = useState(false);
  const viewport = useRef<HTMLDivElement>(null);
  const reducedMotion = useExperienceStore((state) => state.reducedMotion);
  const artifact = digitalArtifacts[active];

  useEffect(() => {
    if (reducedMotion || !viewport.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setSceneActive(entry.isIntersecting);
        if (entry.isIntersecting) {
          setSceneReady(true);
        }
      },
      { rootMargin: '350px 0px' },
    );
    observer.observe(viewport.current);
    return () => observer.disconnect();
  }, [reducedMotion]);

  const move = (direction: number) => {
    setActive((current) => (current + direction + digitalArtifacts.length) % digitalArtifacts.length);
  };

  return (
    <section id="artefactos" className="artifacts section" aria-labelledby="artifacts-title">
      <div className="section-index reveal"><span>03</span><p>GALERÍA DE OBJETOS DIGITALES</p><i /></div>
      <div className="artifact-heading reveal">
        <div>
          <span className="eyebrow">Colección procedural / cinco artefactos</span>
          <h2 id="artifacts-title">Ideas que también<br /><em>ocupan espacio.</em></h2>
        </div>
        <p>Objetos digitales, escenas vivas y sistemas que responden. Cada pieza sintetiza una forma distinta de diseñar interacción.</p>
      </div>

      <div className="artifact-shell reveal">
        <div ref={viewport} className="artifact-viewport" aria-label={`Objeto 3D: ${artifact.title}`}>
          <div className="artifact-grid-lines" />
          {reducedMotion ? (
            <div className="artifact-static" style={{ '--artifact-color': artifact.color } as CSSProperties}>
              <div /><div /><span>{artifact.code}</span>
            </div>
          ) : sceneReady ? (
            <Suspense fallback={<div className="artifact-loading"><span /><p>Construyendo objeto…</p></div>}>
              <ArtifactScene active={active} rendering={sceneActive} />
            </Suspense>
          ) : (
            <div className="artifact-loading"><span /><p>Objeto en espera…</p></div>
          )}
          <div className="artifact-hud">
            <span>CAMPO / {String(active + 1).padStart(2, '0')}</span>
            <span>SELECCIÓN MANUAL / ÓPTICA ACTIVA</span>
          </div>
          <div className="artifact-axis"><i /><i /><i /></div>
        </div>

        <div className="artifact-console">
          <header><span>ARCHIVO DE OBJETOS / NODO</span><strong>{artifact.code}</strong></header>
          <AnimatePresence mode="wait">
            <motion.div
              key={artifact.id}
              className="artifact-copy"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
            >
              <span>{artifact.type}</span>
              <h3>{artifact.title}</h3>
              <p>{artifact.description}</p>
              <dl><dt>COMPORTAMIENTO</dt><dd>{artifact.behavior}</dd><dt>MATERIA</dt><dd>Geometría generativa / tiempo real</dd></dl>
            </motion.div>
          </AnimatePresence>
          <div className="artifact-controls">
            <button onClick={() => move(-1)} aria-label="Objeto anterior"><ChevronLeft /></button>
            <div role="tablist" aria-label="Seleccionar artefacto">
              {digitalArtifacts.map((item, index) => (
                <button
                  key={item.id}
                  className={index === active ? 'active' : ''}
                  onClick={() => setActive(index)}
                  aria-label={`Ver ${item.title}`}
                  aria-selected={index === active}
                  role="tab"
                >
                  {String(index + 1).padStart(2, '0')}
                </button>
              ))}
            </div>
            <button onClick={() => move(1)} aria-label="Objeto siguiente"><ChevronRight /></button>
          </div>
        </div>
      </div>
    </section>
  );
}

export function CinematicScroll() {
  const section = useRef<HTMLElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const reducedMotion = useExperienceStore((state) => state.reducedMotion);

  useEffect(() => {
    if (reducedMotion || !section.current || !stage.current) return;
    const media = gsap.matchMedia();
    const context = gsap.context(() => {
      media.add('(min-width: 901px)', () => {
        const timeline = gsap.timeline({
          scrollTrigger: {
            trigger: section.current,
            start: 'top top',
            end: '+=2800',
            scrub: 1.1,
            pin: stage.current,
            anticipatePin: 1,
          },
        });
        timeline
          .to('.cine-flat-card', { scale: 0.82, rotationX: 12, y: -20, duration: 1 })
          .to('.cine-layer.layer-a', { x: -170, y: -90, rotateY: -18, opacity: 1, duration: 1 }, '<')
          .to('.cine-layer.layer-b', { x: 160, y: 80, rotateY: 18, opacity: 1, duration: 1 }, '<')
          .to('.cine-step.s2', { opacity: 1, color: '#d8ff3e', duration: 0.25 }, '<')
          .to('.cine-orbit-system', { scale: 1, opacity: 1, rotate: 90, duration: 1.2 })
          .to('.cine-layer.layer-c', { z: 130, y: -135, opacity: 1, duration: 1 }, '<')
          .to('.cine-step.s3', { opacity: 1, color: '#d8ff3e', duration: 0.25 }, '<')
          .to('.cine-flat-card', { backgroundColor: '#111116', boxShadow: '0 50px 120px rgba(0,0,0,.55)', duration: 0.8 })
          .to('.cine-interface-copy', { opacity: 1, y: 0, duration: 0.7 }, '<')
          .to('.cine-step.s4', { opacity: 1, color: '#d8ff3e', duration: 0.25 }, '<')
          .to('.cine-progress i', { width: '100%', duration: 3.7 }, 0);
      });
    }, section);
    return () => {
      media.revert();
      context.revert();
    };
  }, [reducedMotion]);

  return (
    <section ref={section} id="transformacion" className="cinematic-scroll" aria-labelledby="cinematic-title">
      <div ref={stage} className="cinematic-stage">
        <div className="cinematic-copy">
          <span className="eyebrow">Secuencia 04 / transformación</span>
          <h2 id="cinematic-title">De superficie<br />estática a<br /><em>sistema vivo.</em></h2>
          <p>El scroll no avanza una página. Desarma una interfaz y revela la arquitectura que estaba esperando debajo.</p>
          <div className="cine-steps">
            <span className="cine-step s1">01 / SUPERFICIE</span>
            <span className="cine-step s2">02 / CAPAS</span>
            <span className="cine-step s3">03 / ESPACIO</span>
            <span className="cine-step s4">04 / RESPUESTA</span>
          </div>
        </div>
        <div className="cinematic-object" aria-label="Interfaz estática transformándose en una experiencia dimensional">
          <div className="cine-orbit-system"><i /><i /><i /></div>
          <div className="cine-layer layer-a"><span>NARRATIVA</span></div>
          <div className="cine-layer layer-b"><span>INTERACCIÓN</span></div>
          <div className="cine-layer layer-c"><span>SISTEMA</span></div>
          <div className="cine-flat-card">
            <header><i /><span>NODO / SURFACE-01</span><small>LIVE</small></header>
            <div className="cine-interface-copy">
              <span>EXPERIENCIA EN LÍNEA</span>
              <strong>RESPONDE<br />A TU PRESENCIA.</strong>
              <button>ENTRAR <ArrowRight size={14} /></button>
            </div>
            <div className="cine-placeholder"><i /><i /><i /><i /></div>
          </div>
        </div>
        <div className="cine-progress"><i /></div>
        <span className="cine-scroll-label">SCROLL PARA TRANSFORMAR ↓</span>
      </div>
    </section>
  );
}

const motionModes = [
  { id: 'type', code: 'M-01', label: 'Tipografía cinética', icon: Sparkles },
  { id: 'glass', code: 'M-02', label: 'Materia líquida', icon: CircleDot },
  { id: 'choreo', code: 'M-03', label: 'Coreografía UI', icon: Layers3 },
  { id: 'field', code: 'M-04', label: 'Campo magnético', icon: Orbit },
] as const;

export function MotionLaboratory() {
  const [active, setActive] = useState<(typeof motionModes)[number]['id']>('type');
  const pointerReadout = useRef<HTMLSpanElement>(null);
  const magneticCore = useRef<HTMLDivElement>(null);
  const motionStage = useRef<HTMLDivElement>(null);
  const stageBounds = useRef<DOMRect | null>(null);
  const pendingPoint = useRef({ x: 0, y: 0, labelX: 50, labelY: 50 });
  const pointerFrame = useRef(0);
  const section = useSectionVisibility<HTMLElement>();
  const reducedMotion = useExperienceStore((state) => state.reducedMotion);

  const measureStage = () => {
    stageBounds.current = motionStage.current?.getBoundingClientRect() ?? null;
  };

  const trackPointer = (event: PointerEvent<HTMLDivElement>) => {
    if (active !== 'field' || reducedMotion) return;
    const rect = stageBounds.current ?? event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    pendingPoint.current = {
      x: Math.max(0, Math.min(rect.width, event.clientX - rect.left)) - rect.width / 2,
      y: Math.max(0, Math.min(rect.height, event.clientY - rect.top)) - rect.height / 2,
      labelX: Math.max(0, Math.min(100, x)),
      labelY: Math.max(0, Math.min(100, y)),
    };
    if (pointerFrame.current) return;
    pointerFrame.current = requestAnimationFrame(() => {
      const point = pendingPoint.current;
      magneticCore.current?.style.setProperty('--magnetic-x', `${point.x}px`);
      magneticCore.current?.style.setProperty('--magnetic-y', `${point.y}px`);
      if (pointerReadout.current) {
        pointerReadout.current.textContent = `${Math.round(point.labelX)}:${Math.round(point.labelY)}`;
      }
      pointerFrame.current = 0;
    });
  };

  const resetPointer = () => {
    if (pointerFrame.current) {
      cancelAnimationFrame(pointerFrame.current);
      pointerFrame.current = 0;
    }
    stageBounds.current = null;
    pendingPoint.current = { x: 0, y: 0, labelX: 50, labelY: 50 };
    magneticCore.current?.style.setProperty('--magnetic-x', '0px');
    magneticCore.current?.style.setProperty('--magnetic-y', '0px');
    if (pointerReadout.current) pointerReadout.current.textContent = '50:50';
  };

  useEffect(() => () => cancelAnimationFrame(pointerFrame.current), []);

  return (
    <section ref={section.ref} id="movimiento" className={section.visible ? 'motion-lab section is-visible' : 'motion-lab section'} aria-labelledby="motion-title">
      <div className="section-index reveal"><span>05</span><p>LABORATORIO DE MOVIMIENTO</p><i /></div>
      <div className="motion-lab-layout">
        <div className="motion-lab-menu reveal">
          <span className="eyebrow">Cuatro lenguajes / una intención</span>
          <h2 id="motion-title">Movimiento que<br /><em>guía, no decora.</em></h2>
          <p>Una colección de comportamientos visuales. Cada ritmo cambia qué se mira primero, cuánto dura la atención y cómo se entiende una interfaz.</p>
          <div>
            {motionModes.map((mode) => {
              const Icon = mode.icon;
              return (
                <button key={mode.id} className={active === mode.id ? 'active' : ''} onClick={() => setActive(mode.id)}>
                  <Icon size={17} /><span>{mode.code}</span><strong>{mode.label}</strong><ArrowRight size={15} />
                </button>
              );
            })}
          </div>
        </div>

        <div
          ref={motionStage}
          className={`motion-stage motion-${active} ${reducedMotion ? 'is-static' : ''}`}
          onPointerEnter={measureStage}
          onPointerMove={trackPointer}
          onPointerLeave={resetPointer}
          aria-live="polite"
        >
          <header><span>LAB / {motionModes.find((mode) => mode.id === active)?.code}</span><span>POINTER <b ref={pointerReadout}>50:50</b></span></header>
          <AnimatePresence mode="wait">
            <motion.div key={active} className="motion-demo" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.03 }}>
              {active === 'type' && (
                <div className="kinetic-type">
                  <span>ENTRA</span><span>CAMBIA</span><span>RESPONDE</span>
                  <p>CADA PALABRA OCUPA SU TIEMPO.</p>
                </div>
              )}
              {active === 'glass' && (
                <div className="liquid-system">
                  <div className="liquid-blob a" /><div className="liquid-blob b" />
                  <div className="glass-panel"><span>ÍNDICE DE REFRACCIÓN</span><strong>1.42</strong><p>La materia también puede organizar información.</p></div>
                </div>
              )}
              {active === 'choreo' && (
                <div className="choreo-system">
                  {['Entrada', 'Contexto', 'Detalle', 'Acción'].map((label, index) => <div key={label} style={{ '--order': index } as CSSProperties}><span>0{index + 1}</span><strong>{label}</strong></div>)}
                  <p>UN ELEMENTO PREPARA AL SIGUIENTE.</p>
                </div>
              )}
              {active === 'field' && (
                <div className="magnetic-system">
                  <div ref={magneticCore} className="magnetic-core"><MousePointer2 /><span>ACERCATE</span></div>
                  {Array.from({ length: 12 }, (_, index) => <i key={index} style={{ '--particle': index } as CSSProperties} />)}
                  <p>LA INTERFAZ RECONOCE TU PRESENCIA.</p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
          <footer><span>TIEMPO REAL</span><i /><span>REDUCED MOTION READY</span></footer>
        </div>
      </div>
    </section>
  );
}

export function ExperienceMap() {
  const [active, setActive] = useState(experienceNodes[6].id);
  const selected = experienceNodes.find((node) => node.id === active) ?? experienceNodes[0];

  return (
    <section id="mapa" className="experience-map section" aria-labelledby="map-title">
      <div className="map-heading reveal">
        <div><span className="eyebrow">Constelación de experiencias / sistema 06</span><h2 id="map-title">Nada vive<br /><em>aislado.</em></h2></div>
        <p>Una experiencia digital es un mapa de relaciones: contenido, gesto, información, estado y próximo paso.</p>
      </div>
      <div className="map-shell reveal">
        <div className="constellation" aria-label="Mapa interactivo de tipos de experiencias digitales">
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            <path d="M18 20 L45 34 L70 15 M45 34 L84 48 L68 79 M45 34 L48 67 L25 82 M48 67 L68 79 M48 67 L8 52 M8 52 L18 20 M25 82 L8 52" />
            <circle cx="45" cy="34" r="18" /><circle cx="48" cy="67" r="12" />
          </svg>
          <div className="map-radar" />
          {experienceNodes.map((node) => (
            <button
              key={node.id}
              className={active === node.id ? 'map-node active' : 'map-node'}
              style={{ left: `${node.x}%`, top: `${node.y}%` } as CSSProperties}
              onMouseEnter={() => setActive(node.id)}
              onFocus={() => setActive(node.id)}
              onClick={() => setActive(node.id)}
            >
              <i /><span>{node.label}</span><small>{node.code}</small>
            </button>
          ))}
          <div className="map-center"><span>NODO</span><strong>0</strong></div>
        </div>
        <AnimatePresence mode="wait">
          <motion.aside key={selected.id} className="map-detail" initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }}>
            <header><span>SEÑAL SELECCIONADA</span><strong>{selected.code}</strong></header>
            <div className="map-detail-index">{String(experienceNodes.indexOf(selected) + 1).padStart(2, '0')}</div>
            <span>{selected.label}</span>
            <h3>{selected.description}</h3>
            <div><small>ARQUITECTURA POSIBLE</small><p>{selected.system}</p></div>
            <footer><ScanLine size={16} /><span>CONEXIÓN ESTABLE</span><i /></footer>
          </motion.aside>
        </AnimatePresence>
      </div>
    </section>
  );
}

function BookingDemo() {
  return (
    <div className="living-demo booking-demo">
      <header><span>SEDE PALERMO</span><strong>JUN / 26</strong></header>
      <div className="booking-calendar">{[18, 19, 20, 21, 22, 23, 24].map((day) => <span key={day} className={day === 21 ? 'selected' : ''}>{day}<small>{day === 21 ? 'JUE' : '—'}</small></span>)}</div>
      <div className="booking-time"><span>18:30</span><p>Experiencia seleccionada<br /><strong>Ritual / 60 min</strong></p><Check /></div>
      <button>CONFIRMAR RESERVA <ArrowRight size={14} /></button>
    </div>
  );
}

function ProductDemo() {
  return (
    <div className="living-demo product-demo">
      <header><span>OBJETO / SERIE 04</span><ShoppingBag size={15} /></header>
      <div className="product-object"><i /><i /><i /></div>
      <div><span>NEGRO / MATE</span><strong>FORMA 04</strong><p>Material reactivo a la luz.</p></div>
      <footer><span>01</span><i /><span>04</span></footer>
    </div>
  );
}

function DashboardDemo() {
  return (
    <div className="living-demo dashboard-demo">
      <header><span>PULSO COMERCIAL</span><strong>EN VIVO <i /></strong></header>
      <div className="dashboard-value"><span>CONSULTAS CALIFICADAS</span><strong>+38.4%</strong></div>
      <div className="dashboard-chart">{[42, 58, 46, 75, 68, 88, 96, 83, 100, 112].map((height, index) => <i key={index} style={{ '--bar': `${height}px`, '--order': index } as CSSProperties} />)}</div>
      <footer><span>HOY</span><span>30 DÍAS</span><span>90 DÍAS</span></footer>
    </div>
  );
}

function LaunchDemo() {
  return (
    <div className="living-demo launch-demo">
      <header><span>ACCESO / 27.06</span><CircleDot size={15} /></header>
      <div className="launch-orbit"><i /><i /><span>07</span></div>
      <div className="countdown"><span><strong>08</strong>DÍAS</span><span><strong>14</strong>HORAS</span><span><strong>37</strong>MIN</span></div>
      <p>PRÓXIMA SEÑAL ENTRANTE</p>
    </div>
  );
}

export function LivingInterfaces() {
  const [paused, setPaused] = useState(false);
  const section = useSectionVisibility<HTMLElement>();
  return (
    <section ref={section.ref} id="interfaces" className={`living-interfaces section${paused ? ' paused' : ''}${section.visible ? ' is-visible' : ''}`} aria-labelledby="interfaces-title">
      <div className="section-index reveal"><span>07</span><p>INTERFACES VIVAS</p><i /></div>
      <div className="living-heading reveal">
        <div><span className="eyebrow">Micro sistemas / escala humana</span><h2 id="interfaces-title">Pequeñas superficies.<br /><em>Mucho comportamiento.</em></h2></div>
        <div><p>Reservar, explorar, leer datos o esperar un lanzamiento. Cuatro interfaces compactas con ritmos completamente distintos.</p><button onClick={() => setPaused(!paused)}>{paused ? <Play size={15} /> : <Pause size={15} />}{paused ? 'Reanudar muestras' : 'Pausar muestras'}</button></div>
      </div>
      <div className="living-grid">
        {livingInterfaces.map((item) => (
          <article key={item.id} className="living-card reveal">
            <div className="living-card-meta"><span>{item.code}</span><small>INTERFAZ ACTIVA</small></div>
            {item.id === 'booking' && <BookingDemo />}
            {item.id === 'product' && <ProductDemo />}
            {item.id === 'dashboard' && <DashboardDemo />}
            {item.id === 'launch' && <LaunchDemo />}
            <footer><h3>{item.title}</h3><p>{item.description}</p><Box size={17} /></footer>
          </article>
        ))}
      </div>
    </section>
  );
}
